// A workload's folded state in plain words (02-DESIGN/03-console/
// 01-the-console.md § the words: a placement's status as a plain state —
// running, starting, stopped — and *Remove* · *Stop* · *Start* for retire,
// suspend and resume). The wire keeps its words; the page shows these.

import type { Tone } from "../../ui/components.tsx";
import {
  type Event,
  EventKind,
  type PlatformName,
  Reason,
  RegistrationState,
  type SlotState,
  Status,
  type Workload,
} from "../../wire/workloads.ts";

/** Plain is a state as the page says it. */
export interface Plain {
  word: string;
  tone: Tone;
  /** Whether it belongs on Home under *needs you*. */
  attention: boolean;
}

/**
 * platformWorkload finds one of the platform's own workloads in the workspace
 * — its vault, its state service — among its workloads: the registration
 * carrying that platform name, not removed (impire-workloads/wire/wire.go:
 * Registration.Platform; decision 0018). It is never recognised by its image.
 */
export function platformWorkload(all: readonly Workload[], name: PlatformName): Workload | undefined {
  return all.find((w) => w.state !== RegistrationState.retired && w.platform === name);
}

/** plainState reads a workload's registration and fold into one plain word. */
export function plainState(w: Pick<Workload, "state" | "placement">): Plain {
  if (w.state === RegistrationState.retired) {
    return { word: "removed", tone: "neutral", attention: false };
  }
  if (w.state === RegistrationState.suspended) {
    return { word: "stopped", tone: "neutral", attention: false };
  }
  return plainStatus(w.placement.status);
}

/** plainStatus is a fold status — the workload's summary or one slot's — as one plain word. */
export function plainStatus(status: string): Plain {
  switch (status) {
    case Status.running:
      return { word: "running", tone: "live", attention: false };
    case Status.pending:
    case Status.placing:
      return { word: "starting", tone: "info", attention: false };
    case Status.failing:
      return { word: "failing", tone: "bad", attention: true };
    case Status.unschedulable:
      return { word: "cannot start", tone: "bad", attention: true };
    case Status.suspended:
      return { word: "stopped", tone: "neutral", attention: false };
    case Status.retired:
      return { word: "removed", tone: "neutral", attention: false };
    default:
      return { word: status, tone: "neutral", attention: false };
  }
}

/** plainEvent is one lifecycle event as the page says it. */
export function plainEvent(kind: string): { word: string; tone: Tone } {
  switch (kind) {
    case EventKind.booting:
      return { word: "starting", tone: "info" };
    case EventKind.running:
      return { word: "running", tone: "live" };
    case EventKind.died:
      return { word: "died", tone: "bad" };
    case EventKind.failing:
      return { word: "failing", tone: "bad" };
    case EventKind.assigned:
      return { word: "assigned", tone: "neutral" };
    case EventKind.released:
      return { word: "released", tone: "neutral" };
    case EventKind.suspended:
      return { word: "stopped", tone: "neutral" };
    case EventKind.resumed:
      return { word: "started", tone: "info" };
    case EventKind.retired:
      return { word: "removed", tone: "neutral" };
    case EventKind.unschedulable:
      return { word: "cannot start", tone: "bad" };
    case EventKind.scaled:
      return { word: "copy closed", tone: "neutral" };
    case EventKind.changeFailed:
      return { word: "change undone", tone: "bad" };
    case EventKind.namesDeclared:
      return { word: "names taken", tone: "neutral" };
    case EventKind.namesRefused:
      return { word: "names refused", tone: "bad" };
    case EventKind.imageFollowed:
      return { word: "image followed", tone: "info" };
    default:
      return { word: kind, tone: "neutral" };
  }
}

/** copyName is a slot as the page says it: slots count from zero, copies from one. */
export function copyName(slot: number): string {
  return `copy ${String(slot + 1)}`;
}

/**
 * slotsOf is a workload's slots in order (decision 0035). A fold written
 * before slots carried none: its one slot is the summary.
 */
export function slotsOf(w: Pick<Workload, "placement">): SlotState[] {
  const p = w.placement;
  if (p.slots !== undefined && p.slots.length > 0) {
    return p.slots;
  }
  return [
    {
      slot: 0,
      placement: p.placement,
      executor: p.executor,
      status: p.status,
      failures: p.failures,
      reason: p.reason,
      message: p.message,
    },
  ];
}

/** copies is how many slots a workload runs as: the fold's count, else the registration's, else one. */
export function copies(w: Pick<Workload, "placement" | "replicas">): number {
  const n = w.placement.replicas ?? w.replicas ?? 0;
  return n > 0 ? n : 1;
}

/**
 * runningCount is the summary's count where a workload runs as more than one
 * copy (04-placement-records.md § the console surface: "n of m running"),
 * and empty where it runs as one.
 */
export function runningCount(w: Pick<Workload, "placement" | "replicas" | "state">): string {
  const m = copies(w);
  if (m <= 1 || w.state !== RegistrationState.active) {
    return "";
  }
  return `${String(w.placement.ready ?? 0)} of ${String(m)} running`;
}

/** slotOfPlacement finds which copy holds a placement now, or undefined for one no slot holds. */
export function slotOfPlacement(p: Workload["placement"], placement: string): number | undefined {
  if (placement === "") {
    return undefined;
  }
  return slotsOf({ placement: p }).find((s) => s.placement === placement)?.slot;
}

/**
 * reachLine is what a workload answers as and may reach, said as one counted
 * line (01-the-console.md § conventions: a list of permissions is counted
 * first): *answers as 2 · reaches 3 hosts*; count is zero when it has neither.
 */
export function reachLine(w: Pick<Workload, "names" | "egress">): { line: string; count: number } {
  const names = (w.names ?? []).length;
  const hosts = (w.egress ?? []).length;
  const answers = names === 0 ? "no name" : `answers as ${String(names)}`;
  const reaches =
    hosts === 0
      ? "reaches nothing outside Impire"
      : `reaches ${String(hosts)} ${hosts === 1 ? "host" : "hosts"}`;
  return { line: `${answers} · ${reaches}`, count: names + hosts };
}

/**
 * eventDetail is an event's detail in words: where, which attempt, and why —
 * a change undone names the generation that could not start and the one it is
 * back on (decision 0035).
 */
export function eventDetail(e: Event): string {
  if (e.event === EventKind.changeFailed) {
    const back =
      e.generation !== undefined && e.restored !== undefined
        ? `generation ${String(e.generation)} could not start; back on generation ${String(e.restored)}`
        : "the change could not start; back on the last that ran";
    return e.message !== undefined && e.message !== "" ? `${back}: ${e.message}` : back;
  }
  if (e.event === EventKind.scaled) {
    return "the count no longer wants this copy";
  }
  return [
    e.executor !== undefined && e.executor !== "" ? `on ${e.executor}` : "",
    e.attempt !== undefined && e.attempt > 0 ? `attempt ${String(e.attempt)}` : "",
    e.message !== undefined && e.message !== ""
      ? e.message
      : e.reason !== undefined && e.reason !== ""
        ? e.event === EventKind.unschedulable
          ? reasonWords(e.reason)
          : `(${e.reason})`
        : "",
  ]
    .filter((x) => x !== "")
    .join(" ");
}

/**
 * generationLine is a registration's generation and the last good one in
 * words (decision 0035): empty before its first change, the last good named
 * only where it is not the generation itself.
 */
export function generationLine(w: Pick<Workload, "generation" | "last_good">): string {
  const g = w.generation ?? 0;
  if (g === 0) {
    return "";
  }
  const good = w.last_good?.generation;
  if (good === undefined) {
    return `generation ${String(g)}, not yet run`;
  }
  return good === g
    ? `generation ${String(g)}`
    : `generation ${String(g)} · last ran generation ${String(good)}`;
}

/**
 * reasonWords says why an auction closed empty where the service gave no
 * message (decision 0034): no executor, no host of the image's architecture,
 * a required capability no host advertises, or no room.
 */
export function reasonWords(reason: string): string {
  switch (reason) {
    case Reason.noExecutor:
      return "there is no host to run it on";
    case Reason.architecture:
      return "no host runs the image's architecture";
    case Reason.requires:
      return "no host has a capability it requires";
    case Reason.capacity:
      return "no host that could carry it has room";
    default:
      return reason;
  }
}

/**
 * whyNot is why a workload or one copy cannot start, as the workload service
 * names it beside the unschedulable status (decision 0034; hq issue 132): its
 * message, which names what is missing, else the reason in words; empty for
 * any other status. A workload's summary without one takes the first copy's.
 */
export function whyNot(
  x: Pick<SlotState, "status" | "reason" | "message"> & { slots?: SlotState[] },
): string {
  if (x.status !== Status.unschedulable) {
    return "";
  }
  if (x.message !== undefined && x.message !== "") {
    return x.message;
  }
  if (x.reason !== undefined) {
    return reasonWords(x.reason);
  }
  for (const sl of x.slots ?? []) {
    const why = sl.status === Status.unschedulable ? whyNot(sl) : "";
    if (why !== "") {
      return why;
    }
  }
  return "";
}
